import { servicePath } from '../enum/routes';
import { OUTDOOR_SPECIALTY_SERVICES } from './outdoorSpecialties';

export type FaqItem = { question: string; answer: string };

export type CostTier = {
  title: string;
  intro: string;
  rows: { tier: string; cost: string; includes: string }[];
};

export type ServiceDef = {
  slug: string;
  name: string;
  shortName: string;
  eyebrow: string;
  heroTitle: string;
  heroLead: string;
  heroImage: string;
  heroImageAlt: string;
  metaTitle: string;
  metaDescription: string;
  introTitle: string;
  introParagraphs: string[];
  projectTypesTitle: string;
  projectTypes: string[];
  whyTitle: string;
  whyItems: string[];
  processTitle: string;
  processSteps: { label: string; body: string }[];
  galleryEyebrow: string;
  galleryTitle: string;
  gallery: { src: string; alt: string }[];
  cost?: CostTier;
  faqs: FaqItem[];
  ctaTitle: string;
  ctaLead: string;
  /** When false, service stays routable but is omitted from primary nav/footer lists */
  showInNav?: boolean;
};

export const SERVICES: ServiceDef[] = [
  {
    slug: 'kitchen-remodeling',
    name: 'Kitchen Remodeling',
    shortName: 'Kitchen',
    eyebrow: 'Texas Kitchen Remodeling',
    heroTitle: 'Kitchen Remodeling Quotes in Texas',
    heroLead:
      "From a cabinet-and-countertop refresh to a full gut. Tell us what you're planning and we'll help you take the next step with a free on-site estimate.",
    heroImage:
      'https://images.unsplash.com/photo-1682888818650-0a7cbb35287d?w=1920&q=80&auto=format&fit=crop',
    heroImageAlt: 'Custom kitchen with white cabinets and a stone island',
    metaTitle: 'Kitchen Remodeling in Texas | Cost, Process & Quotes | Epic Home Pros',
    metaDescription:
      'Plan a kitchen remodel in Texas with clear cost ranges, process steps, and layout guidance. Free estimates in Austin, Dallas, Houston, San Antonio, and nearby metros.',
    introTitle: 'From a fresh update to a complete kitchen transformation',
    introParagraphs: [
      "Tell us what you're planning and we'll help bring the kitchen together - cabinetry, stone, tile, plumbing, electrical, and finish work.",
      "You'll get an on-site visit and a written estimate. From there, you decide whether to move forward. Free estimates. No pressure.",
      'We serve Austin, Dallas, Houston, San Antonio, and surrounding metros. As of 2026, Texas kitchen planning ranges run about $10,000-$25,000 for a minor refresh, $25,000-$45,000 cosmetic, $45,000-$90,000 mid-range, $90,000-$150,000 high-end, and $150,000+ for luxury or structural work. Your written estimate will price your house, not a national average.',
    ],
    projectTypesTitle: 'Kitchen projects we commonly handle',
    projectTypes: [
      'Cosmetic refreshes: doors or refacing, counters, backsplash, fixtures, same layout',
      'Full mid-range remodels: new cabinets, quartz or stone, appliances, flooring',
      'High-end and custom cabinetry with layout changes and designer lighting',
      'Structural guts with relocated plumbing, gas, and opened walls',
      'Island additions, pantry builds, and open-concept kitchen-living connections',
    ],
    whyTitle: 'Why homeowners start here',
    whyItems: [
      'One form instead of five cold calls.',
      'On-site estimates grounded in your Texas home.',
      'An on-site estimate at your kitchen, not a phone guess.',
      'Reply within 1 business day.',
      'Free estimate. No obligation to move forward.',
    ],
    processTitle: 'What happens after you request a quote',
    processSteps: [
      {
        label: '1. Request',
        body: 'You send the kitchen details, photos if you have them, and a preferred visit time.',
      },
      {
        label: '2. On-site visit',
        body: "We'll schedule an on-site visit to review your kitchen and discuss next steps.",
      },
      {
        label: '3. Get your estimate',
        body: 'We measure, talk through the layout, and send a written estimate. You decide whether to move forward.',
      },
    ],
    galleryEyebrow: 'Kitchens',
    galleryTitle: 'The rooms people are planning.',
    gallery: [
      {
        src: 'https://images.unsplash.com/photo-1556912173-46c336c7fd55?w=1200&q=80&auto=format&fit=crop',
        alt: 'White modern kitchen',
      },
      {
        src: 'https://images.unsplash.com/photo-1682888818650-0a7cbb35287d?w=1200&q=80&auto=format&fit=crop',
        alt: 'Marble kitchen island',
      },
      {
        src: 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=1200&q=80&auto=format&fit=crop',
        alt: 'Open kitchen with wood cabinets and island seating',
      },
    ],
    cost: {
      title: '2026 Texas Kitchen Remodeling Cost Guide',
      intro:
        'These are planning ranges for Texas kitchen projects in 2026 - not a quote for your house. Your written estimate will itemize the real number.',
      rows: [
        {
          tier: 'Minor / Refresh',
          cost: '$10,000 - $25,000',
          includes: 'Paint, hardware, fixtures, limited countertop or cabinet work. Same layout.',
        },
        {
          tier: 'Cosmetic',
          cost: '$25,000 - $45,000',
          includes: 'Refacing or repainting, counters, backsplash, lighting. Mostly same footprint.',
        },
        {
          tier: 'Mid-Range',
          cost: '$45,000 - $90,000',
          includes: 'New cabinets, countertops, appliances, flooring, lighting.',
        },
        {
          tier: 'High-End',
          cost: '$90,000 - $150,000',
          includes: 'Custom cabinetry, premium stone, premium appliances, layout changes.',
        },
        {
          tier: 'Luxury / Structural',
          cost: '$150,000+',
          includes: 'Full gut, walls, plumbing or gas relocation, structural work.',
        },
      ],
    },
    faqs: [
      {
        question: 'What kitchen remodel details should I share in the quote form?',
        answer:
          'Share your city, whether you want a cosmetic refresh or a full gut, and any must-haves (island, new layout, appliances). Photos of the current kitchen help us prepare for the visit.',
      },
      {
        question: 'Do I get a phone quote or an on-site estimate?',
        answer:
          'We schedule an on-site visit, measure the kitchen, and prepare a written estimate. Phone ranges are only a starting point - your house sets the real number.',
      },
      {
        question: 'Is the estimate free? Am I obligated to hire anyone?',
        answer:
          'Estimates are free. There is no obligation to move forward after you receive a quote.',
      },
      {
        question: 'Which Texas cities do you cover for kitchen remodeling?',
        answer:
          'We serve kitchen projects in Austin, Dallas, Houston, San Antonio, and surrounding metros such as Fort Worth, Arlington, and Houston-area suburbs. Request a quote and we will confirm coverage for your address.',
      },
    ],
    ctaTitle: 'Ready for a kitchen quote?',
    ctaLead: 'Free estimate. No obligation.',
    showInNav: true,
  },
  {
    slug: 'bathroom-remodeling',
    name: 'Bathroom Remodeling',
    shortName: 'Bathroom',
    eyebrow: 'Texas Bathroom Remodeling',
    heroTitle: 'Bathroom Remodeling Quotes in Texas',
    heroLead:
      'Walk-in showers, soaking tubs, and full bathroom renovations. Get a free on-site estimate.',
    heroImage:
      'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1920&q=80&auto=format&fit=crop',
    heroImageAlt: 'Marble bathroom with walk-in shower',
    metaTitle: 'Bathroom Remodeling Quotes in Texas | Epic Home Pros',
    metaDescription:
      'Free bathroom remodeling estimates in Texas. Free estimates for walk-in showers, primary suites, and hall baths in Austin, Dallas, Houston, San Antonio, and nearby cities.',
    introTitle: 'Bathroom remodeling without calling a dozen crews',
    introParagraphs: [
      "Tell us what you're planning - a hall bath, a primary suite, or both - and we'll follow up to schedule a visit, look at the space, and put together an estimate in Austin, Dallas, Houston, San Antonio, or a nearby metro.",
      'Typical Texas bath work in 2026: a cosmetic refresh often lands $12,000-$25,000. A full hall bath remodel is often $25,000-$50,000. A primary suite with a walk-in shower and soaking tub can run $45,000-$90,000+. Waterproofing and plumbing moves are what swing the number.',
      "Wet areas need careful waterproofing and code-compliant plumbing. We'll check access, existing lines, and finish choices before locking a written estimate.",
    ],
    projectTypesTitle: 'Bathroom projects we commonly handle',
    projectTypes: [
      'Walk-in showers, tub-to-shower conversions, and soaking tubs',
      'Tile, waterproofing, vanities, and stone tops',
      'Plumbing and electrical updates to current code',
      'Primary suites and hall baths',
      'Accessibility-minded layouts and curb-free shower entries where the space allows',
    ],
    whyTitle: 'Why homeowners start here',
    whyItems: [
      'One request instead of shopping cold-call lists.',
      'On-site bath estimates for your metro.',
      'On-site measuring of wet walls and plumbing access.',
      'Written estimate - you decide what happens next.',
      'Free estimate with no obligation.',
    ],
    processTitle: 'How your quote works',
    processSteps: [
      {
        label: 'Request',
        body: 'Tell us hall bath, primary suite, or both, plus city and timing.',
      },
      {
        label: 'On-site visit',
        body: "We'll schedule an on-site visit to review the bath and discuss next steps.",
      },
      {
        label: 'Get your estimate',
        body: "We'll measure wet walls, check plumbing access, and quote in writing. You decide what happens next.",
      },
    ],
    galleryEyebrow: 'Baths',
    galleryTitle: 'Finished rooms, not mood boards.',
    gallery: [
      {
        src: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1200&q=80&auto=format&fit=crop',
        alt: 'Walk-in shower',
      },
      {
        src: 'https://images.unsplash.com/photo-1507652313519-d4e9174996dd?w=1200&q=80&auto=format&fit=crop',
        alt: 'Soaking tub',
      },
      {
        src: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=1200&q=80&auto=format&fit=crop',
        alt: 'Vanity bath',
      },
    ],
    cost: {
      title: 'Typical Texas bathroom remodel cost (2026)',
      intro:
        'These are typical Texas market planning ranges for 2026 from our existing site guidance - not a quote for your house. Waterproofing and plumbing moves are what swing the number.',
      rows: [
        {
          tier: 'Cosmetic refresh',
          cost: '$12,000 - $25,000',
          includes: 'Surfaces, fixtures, and vanity updates in the same layout.',
        },
        {
          tier: 'Full hall bath',
          cost: '$25,000 - $50,000',
          includes: 'Full remodel of a standard hall bath with tile and waterproofing.',
        },
        {
          tier: 'Primary suite',
          cost: '$45,000 - $90,000+',
          includes: 'Walk-in shower, soaking tub options, dual vanities, larger footprint.',
        },
      ],
    },
    faqs: [
      {
        question: 'Hall bath vs. primary suite - does it matter for the quote?',
        answer:
          'Yes. Primary suites often involve larger footprints, dual vanities, and more finish choices. Hall baths are usually tighter on space and budget. Tell us which room (or both) so we scope the visit correctly.',
      },
      {
        question: 'What usually drives bathroom remodel cost in Texas?',
        answer:
          'Waterproofing, plumbing moves, tile labor, and fixture quality are the big swingers. The ranges on this site are typical 2026 Texas market context - your written estimate will itemize your house.',
      },
      {
        question: 'Can I convert a tub to a walk-in shower?',
        answer:
          'Many Texas homes do tub-to-shower conversions. An on-site visit confirms drain location, waterproofing, and whether the pan and walls need a full rebuild.',
      },
      {
        question: 'How fast will someone respond?',
        answer:
          'We aim to reply within one business day after you submit a quote request.',
      },
      {
        question: 'Is the bathroom estimate free?',
        answer: 'Yes. Free on-site estimate with no obligation to move forward.',
      },
      {
        question: 'Which Texas cities do you cover for bathroom remodeling?',
        answer:
          'We serve bathroom projects in Austin, Dallas, Houston, San Antonio, and surrounding metros when coverage allows. Include your city on the quote form.',
      },
    ],
    ctaTitle: 'Ready for a bath quote?',
    ctaLead: 'Free estimate. No obligation.',
    showInNav: true,
  },
  {
    slug: 'adu-construction',
    name: 'ADU Construction',
    shortName: 'ADU',
    eyebrow: 'Texas ADU Construction',
    heroTitle: 'ADU quotes, with free on-site estimates',
    heroLead:
      'Detached, attached, and garage-conversion ADUs. Create more space for family, guests, or future plans.',
    heroImage:
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80&auto=format&fit=crop',
    heroImageAlt: 'Backyard home and main house',
    metaTitle: 'ADU Construction Quotes in Texas | Epic Home Pros',
    metaDescription:
      'Free ADU construction estimates in Texas for detached cottages, garage conversions, and in-law suites across major Texas metros.',
    introTitle: 'A backyard home is a permitting project, not a weekend build',
    introParagraphs: [
      "Tell us about the lot and what you're hoping to add. We'll schedule a visit to walk setbacks and utilities, then put together a path through design, permits, and construction.",
      "Texas ADUs in 2026 often land $180,000-$350,000+ depending on size, site work, and finishes. Garage conversions can be less. Setbacks, utilities, and parking rules vary by city. We'll walk the lot and tell you what is realistic.",
      'Epic Home Pros is the first step: one request, a lot visit when needed, and a written estimate so you can decide whether to move forward.',
    ],
    projectTypesTitle: 'Project types we handle',
    projectTypes: [
      'Detached backyard cottages',
      'Garage conversions',
      'Attached in-law suites',
      'Guest houses for family use or future plans',
      'Site evaluation for setbacks, utilities, and access',
    ],
    whyTitle: 'Why start here',
    whyItems: [
      'ADUs involve design, permits, and site constraints - local knowledge matters.',
      'On-site ADU evaluations in your metro.',
      'Lot walk before you commit to a path.',
      'Written estimate. You decide next steps.',
      'Free estimate with no obligation.',
    ],
    processTitle: 'How your quote works',
    processSteps: [
      {
        label: 'Request',
        body: 'Share lot notes, city, and whether this is a conversion or new build.',
      },
      {
        label: 'On-site visit',
        body: "We'll schedule a lot visit to review setbacks, utilities, and next steps.",
      },
      {
        label: 'Get your estimate',
        body: "We'll walk setbacks and utilities, then quote a path through design and permits. You decide whether to move forward.",
      },
    ],
    galleryEyebrow: 'ADUs',
    galleryTitle: 'Backyard homes, not renderings.',
    gallery: [
      {
        src: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80&auto=format&fit=crop',
        alt: 'Modern house exterior',
      },
      {
        src: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80&auto=format&fit=crop',
        alt: 'Cottage ADU',
      },
      {
        src: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&q=80&auto=format&fit=crop',
        alt: 'Guest suite',
      },
    ],
    cost: {
      title: 'Typical Texas ADU cost context (2026)',
      intro:
        'These are typical Texas market planning ranges for 2026 from our existing site guidance - not a quote for your lot. Size, site work, utilities, and finishes move the number.',
      rows: [
        {
          tier: 'Garage conversion',
          cost: 'Often less than new build',
          includes: 'Conversion path with insulation, egress, and utility upgrades as needed.',
        },
        {
          tier: 'New detached / attached',
          cost: '$180,000 - $350,000+',
          includes: 'New structure with site work, utilities, and finish level driving the range.',
        },
      ],
    },
    faqs: [
      {
        question: 'Do ADU rules differ by Texas city?',
        answer:
          'Yes. Setbacks, parking, height, and utility connections vary by city and sometimes by neighborhood. An on-site lot walk should happen before you lock a plan.',
      },
      {
        question: 'Garage conversion or new detached build - which is simpler?',
        answer:
          'Garage conversions can involve less new foundation work, but still need proper insulation, egress, and utility upgrades. Detached builds add site work and often more permitting. We can compare both for your lot.',
      },
      {
        question: 'What should I include in my ADU quote request?',
        answer:
          'City, lot notes (size, alley or side access, existing garage), and whether you want a conversion or new structure. Photos of the backyard help.',
      },
      {
        question: 'Is the ADU estimate free?',
        answer:
          'Yes. Estimates are free, and you are not obligated to move forward after the visit.',
      },
    ],
    ctaTitle: 'Ready for an ADU quote?',
    ctaLead: 'Free estimate. No obligation.',
    showInNav: true,
  },
  ...OUTDOOR_SPECIALTY_SERVICES,
  {
    slug: 'outdoor-living',
    name: 'Outdoor Living',
    shortName: 'Outdoor',
    eyebrow: 'Texas Outdoor Living',
    heroTitle: 'Decks, patios, and pergolas built for Texas weather',
    heroLead:
      'Decks, patios, pergolas, outdoor kitchens, and spaces designed for enjoying the Texas outdoors.',
    heroImage:
      'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1920&q=80&auto=format&fit=crop',
    heroImageAlt: 'Covered outdoor living patio',
    metaTitle: 'Outdoor Living Quotes in Texas | Epic Home Pros',
    metaDescription:
      'Free outdoor living estimates in Texas for decks, covered patios, pergolas, and outdoor kitchens across major Texas metros.',
    introTitle: 'Outdoor rooms that survive heat, sun, and summer storms',
    introParagraphs: [
      "Tell us what you're planning for the backyard. We'll help you plan decks, covered patios, pergolas, and outdoor kitchens - quoted in cedar, composite, or hardwood for the way Texans actually use a backyard.",
      "A composite deck can start around $8,000-$20,000. Covered patios and outdoor kitchens climb from there based on roof structure, electrical, and gas. We'll price the site, not a catalog photo.",
      'Shade, drainage, and material choice matter in Texas heat. An on-site visit is how you get a realistic scope and written estimate.',
    ],
    projectTypesTitle: 'What we handle',
    projectTypes: [
      'Decks in cedar, composite, and hardwood',
      'Covered patios and screened rooms',
      'Pergolas and shade structures',
      'Outdoor kitchens and fire features',
      'Drainage-aware layouts for Texas rain events',
    ],
    whyTitle: 'Why homeowners start here',
    whyItems: [
      'One request for decks, patios, pergolas, or outdoor kitchens.',
      'On-site outdoor living estimates in your metro.',
      'Site walk for sun, drainage, and access.',
      'Materials chosen for Texas weather - not catalog photos alone.',
      'Free estimate. No obligation.',
    ],
    processTitle: 'How your quote works',
    processSteps: [
      {
        label: 'Request',
        body: 'Deck, patio, pergola, or outdoor kitchen - plus city and a rough size.',
      },
      {
        label: 'On-site visit',
        body: "We'll schedule an on-site visit to review the yard and discuss next steps.",
      },
      {
        label: 'Get your estimate',
        body: "We'll walk drainage, sun, and access, then quote materials for Texas weather. You decide whether to move forward.",
      },
    ],
    galleryEyebrow: 'Outdoors',
    galleryTitle: 'Shade, fire, and a place to sit.',
    gallery: [
      {
        src: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&q=80&auto=format&fit=crop',
        alt: 'Patio lounge',
      },
      {
        src: 'https://images.unsplash.com/photo-1600585153490-76fb20a32601?w=1200&q=80&auto=format&fit=crop',
        alt: 'Deck',
      },
      {
        src: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200&q=80&auto=format&fit=crop',
        alt: 'Covered patio',
      },
    ],
    faqs: [
      {
        question: 'Composite vs. wood decks in Texas - what should I ask about?',
        answer:
          'Ask about heat, sun exposure, maintenance, and how the structure handles summer storms. Compare cedar, composite, and hardwood for your yard and budget during the site visit.',
      },
      {
        question: 'Do outdoor kitchens need gas and electrical upgrades?',
        answer:
          'Often yes. Outdoor kitchens may need gas lines, outdoor-rated electrical, and proper clearances. That is why an on-site visit matters before a firm number.',
      },
      {
        question: 'What details help with an outdoor living quote?',
        answer:
          'City, project type (deck, patio, pergola, outdoor kitchen), rough size, and whether you need shade cover. Photos of the yard help.',
      },
      {
        question: 'Is the outdoor estimate free?',
        answer: 'Yes. Free estimate, no obligation to move forward.',
      },
    ],
    ctaTitle: 'Ready for an outdoor quote?',
    ctaLead: 'Free estimate. No obligation.',
    showInNav: false,
  },
];

export const SERVICE_BY_SLUG = Object.fromEntries(SERVICES.map((s) => [s.slug, s])) as Record<
  string,
  ServiceDef
>;

/** Primary nav / footer service links - matches HN-style service menu */
export const NAV_SERVICES = SERVICES.filter((s) => s.showInNav !== false);

export const LEGACY_SERVICE_REDIRECTS: Record<string, string> = {
  '/kitchen': servicePath('kitchen-remodeling'),
  '/bathroom': servicePath('bathroom-remodeling'),
  '/adu': servicePath('adu-construction'),
  '/outdoor': servicePath('outdoor-living'),
};

export function getService(slug: string): ServiceDef | undefined {
  return SERVICE_BY_SLUG[slug];
}
